import { useEffect, useState } from "react";
import { api, formatErr, getFileUrl } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Check, X, Eye } from "lucide-react";
import { toast } from "sonner";
import { SkeletonTable } from "../../components/Skeleton";

export default function Reimbursements() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billPreview, setBillPreview] = useState(null); // {path, item, name, amount}

  const load = () => api.get("/reimbursements").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    try { await api.post(`/reimbursements/${id}/${action}`); toast.success(`Request ${action}d`); load(); }
    catch (err) { toast.error(formatErr(err)); }
  };

  const badge = (s) => ({
    pending: "bg-amber-50 text-amber-700 border-amber-300",
    approved: "bg-emerald-50 text-emerald-700 border-emerald-300",
    rejected: "bg-red-50 text-red-700 border-red-300",
  }[s] || "bg-zinc-100 dark:bg-zinc-800");

  const isPdf = (p) => p?.toLowerCase().endsWith(".pdf");

  return (
    <div className="space-y-6" data-testid="admin-reimbursements-page">
      <div>
        <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-indigo-600 mb-2">03 / Approval Queue</div>
        <h1 className="rule-text text-3xl sm:text-4xl text-zinc-900 dark:text-zinc-100">Reimbursements.</h1>
      </div>

      <div className="bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 rounded-xl shadow-sm overflow-x-auto">
        {loading ? <div className="p-4"><SkeletonTable rows={4} cols={7} /></div> : (
          <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Employee</TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Item</TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Amount</TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Date</TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Bill</TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Status</TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-zinc-500 dark:text-zinc-400 text-sm">No reimbursement requests yet.</TableCell></TableRow>
            ) : items.map((r) => (
              <TableRow key={r.id} data-testid={`reimb-row-${r.id}`}>
                <TableCell className="font-bold">{r.employee_name}<div className="text-xs text-zinc-500 dark:text-zinc-400 font-normal">{r.employee_id}</div></TableCell>
                <TableCell>{r.item_name}</TableCell>
                <TableCell className="font-mono">₹ {r.amount.toFixed(2)}</TableCell>
                <TableCell>{r.date}</TableCell>
                <TableCell>
                  {r.bill_file_path ? (
                    <button onClick={() => setBillPreview(r)} className="text-[#4F46E5] hover:underline inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.18em]" data-testid={`view-bill-${r.id}`}>
                      <Eye size={12} /> View
                    </button>
                  ) : <span className="text-zinc-400 text-xs">—</span>}
                </TableCell>
                <TableCell><span className={`eyebrow px-2 py-1 border ${badge(r.status)}`}>{r.status}</span></TableCell>
                <TableCell className="text-right">
                  {r.status === "pending" && (
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="ghost" className="rounded-xl h-8 w-8 p-0 hover:bg-emerald-50 hover:text-emerald-700" onClick={() => act(r.id, "approve")} data-testid={`approve-${r.id}`}><Check size={14} /></Button>
                      <Button size="sm" variant="ghost" className="rounded-xl h-8 w-8 p-0 hover:bg-red-50 hover:text-red-600" onClick={() => act(r.id, "reject")} data-testid={`reject-${r.id}`}><X size={14} /></Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        )}
      </div>

      <Dialog open={!!billPreview} onOpenChange={(o) => !o && setBillPreview(null)}>
        <DialogContent className="rounded-xl max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="rule-text text-2xl">Bill Preview</DialogTitle>
          </DialogHeader>
          {billPreview && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3 text-sm bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-4">
                <div><div className="eyebrow">Employee</div><div className="font-bold mt-1">{billPreview.employee_name}</div></div>
                <div><div className="eyebrow">Item</div><div className="font-bold mt-1">{billPreview.item_name}</div></div>
                <div><div className="eyebrow">Amount</div><div className="font-bold mt-1 font-mono">₹ {billPreview.amount.toFixed(2)}</div></div>
              </div>
              <div className="border border-zinc-200 dark:border-zinc-800 bg-zinc-50 min-h-[400px] flex items-center justify-center">
                {isPdf(billPreview.bill_file_path) ? (
                  <iframe src={getFileUrl(billPreview.bill_file_path)} title="bill" className="w-full h-[600px]" />
                ) : (
                  <img src={getFileUrl(billPreview.bill_file_path)} alt="bill" className="max-w-full max-h-[600px] object-contain" data-testid="bill-image" />
                )}
              </div>
              <div className="flex justify-end gap-2">
                <a href={getFileUrl(billPreview.bill_file_path)} target="_blank" rel="noreferrer">
                  <Button variant="outline" className="rounded-xl uppercase tracking-[0.2em] text-xs font-bold" data-testid="open-bill-new-tab">Open in new tab</Button>
                </a>
                {billPreview.status === "pending" && (
                  <>
                    <Button className="rounded-xl bg-red-600 hover:bg-red-700 text-white uppercase tracking-[0.2em] text-xs font-bold" onClick={() => { act(billPreview.id, "reject"); setBillPreview(null); }} data-testid="modal-reject">Reject</Button>
                    <Button className="klein-btn rounded-xl uppercase tracking-[0.2em] text-xs font-bold" onClick={() => { act(billPreview.id, "approve"); setBillPreview(null); }} data-testid="modal-approve">Approve</Button>
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
